<template>
    <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_PURCHASE_ORDER">
        <div class="purchase-orders-page">
            <header class="purchase-orders-toolbar">
                <div class="toolbar-heading">
                    <h1>Purchase Orders</h1>
                    <span class="result-count" aria-live="polite">{{ totalResults }}</span>
                </div>

                <nav class="purchase-order-tabs" role="tablist" aria-label="Purchase order status">
                    <router-link
                        v-for="tab in purchaseOrderTabs"
                        :key="tab.value"
                        :to="tabRoute(tab.value)"
                        class="purchase-order-tab"
                        :class="{ 'purchase-order-tab--active': selectedTab === tab.value }"
                        role="tab"
                        :aria-label="`${tab.label}: ${tab.description}`"
                        :aria-selected="selectedTab === tab.value ? 'true' : 'false'"
                    >
                        <span>{{ tab.label }}</span>
                    </router-link>
                </nav>

                <router-link to="/stock-import" class="btn-secondary create-order-btn">
                    <i class="material-icons" aria-hidden="true">add</i>
                    Create Purchase Order
                </router-link>
            </header>

            <!-- Enhanced Purchase Order Filters -->
            <PurchaseOrderFilters 
                :search.sync="filters.search"
                :status.sync="filters.status"
                :supplier.sync="filters.supplier"
                :warehouse.sync="filters.warehouse"
                :timeFrame="timeFrame"
                :suppliers="suppliers"
                :showStatusFilter="selectedTab === 'all'"
                @change="onFiltersChange"
                @timeframe-change="onTimeFrameChange"
            />

            <!-- Purchase Orders List -->
            <div v-if="purchaseOrders.length > 0">
                <div class="table-container">
                    <SortableTable 
                        :data="purchaseOrders"
                        :columns="tableColumns"
                        :defaultSort="sortBy"
                        :defaultSortOrder="sortOrder"
                        :itemKey="'id'"
                        :tableStyle="'font-size: 11px; max-width: 100%; width: 100%;'"
                        @sort="onTableSort"
                    >
                    <template #cell-sr_no="{ item, index }">
                        {{ (page - 1) * limit + index + 1 }}
                    </template>
                    
                    <template #cell-name="{ item }">
                        <LinkComponent :to="`/purchase-orders/${item.name}`" class="po-link">
                            {{ item.name }}
                        </LinkComponent>
                    </template>
                    
                    <template #cell-status="{ item }">
                        <span :class="{
                            'badge badge-success': item.completed,
                            'badge badge-danger': item.cancelled,
                            'badge badge-warning': !item.completed && !item.cancelled
                        }">
                            {{ item.completed ? "Completed" : item.cancelled ? "Cancelled" : "Submitted" }}
                        </span>
                    </template>
                    
                    <template #cell-supplier_name="{ item }">
                        {{ item.supplier_name || 'N/A' }}
                    </template>
                    
                    <template #cell-supplier_invoice_no="{ item }">
                        {{ item.supplier_invoice_no || 'N/A' }}
                    </template>
                    
                    <template #cell-target_warehouse_address="{ item }">
                        {{ item.target_warehouse_address || 'N/A' }}
                    </template>
                    
                    <template #cell-created_by="{ item }">
                        {{ item.createdByUser ? (item.createdByUser.full_name || item.createdByUser.name) : 'N/A' }}
                    </template>
                    
                    <template #cell-createdAt="{ item }">
                        {{ item.createdAtFormatted1 || formatDate(item.createdAt) }}
                    </template>
                    
                    <template #cell-submitted_at="{ item }">
                        {{ item.submittedAt || 'N/A' }}
                    </template>
                    
                    <template #cell-eta="{ item }">
                        {{ item.etaFormatted || 'N/A' }}
                    </template>
                    </SortableTable>
                </div>
            </div>
            <div v-else-if="!loadingData" class="text-center py-8">
                <div class="mb-4">
                    <h3 class="text-lg text-gray-600">No {{ activeTabLabel }} Purchase Orders Found</h3>
                    <p class="text-gray-500">{{ emptyStateMessage }}</p>
                </div>
                <router-link 
                    to="/stock-import" 
                    class="btn-secondary text-white px-6 py-3 rounded-lg inline-flex items-center gap-2 transition-colors"
                >
                    <i class="material-icons text-sm">add</i>
                    Create Purchase Order
                </router-link>
            </div>
            <Paginator @change="updatePage" :page="page" :totalPages="totalPages" />
        </div>
    </AllowPermission>
</template>

<script>
import { http } from "@/utils/http"
import { mapState, mapActions } from "vuex"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import Paginator from "../../components/other/Paginator.vue"
import CustomTable from "../../components/layout/CustomTable.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import LinkComponent from "../../components/UI/LinkComponent.vue"
import SortableTable from "../../components/table/SortableTable.vue"
import PurchaseOrderFilters from "../../components/purchase-orders/PurchaseOrderFilters.vue"
import { PERMISSION_CODES } from "../../store/permission"

const PURCHASE_ORDER_TABS = [
    { value: "open", label: "Open", description: "Awaiting completion", status: "1" },
    { value: "completed", label: "Completed", description: "Received orders", status: "2" },
    { value: "all", label: "All", description: "Including cancelled", status: "" },
]

export default {
    name: "purchase-orders",
    data() {
        return {
            page: 1,
            limit: 15,
            totalResults: 0,
            totalPages: 1,
            loadingData: false,
            selectedTab: "open",
            purchaseOrderTabs: PURCHASE_ORDER_TABS,
            purchaseOrders: [],
            suppliers: [],
            timeFrame: {
                startDate: '',
                endDate: '',
                period: ''
            },
            filters: {
                search: "",
                status: "",
                supplier: "",
                warehouse: ""
            },
            sortBy: 'createdAt',
            sortOrder: 'desc',
            tableColumns: [
                { key: 'sr_no', title: '#', width: '50px', sortable: false },
                { key: 'name', title: 'PO Name', width: '120px' },
                { key: 'status', title: 'Status', width: '90px', sortable: false },
                { key: 'supplier_name', title: 'Supplier', width: '120px' },
                { key: 'supplier_invoice_no', title: 'Invoice #', width: '100px' },
                { key: 'target_warehouse_address', title: 'Warehouse', width: '110px' },
                { key: 'created_by', title: 'Created By', width: '100px', sortable: false },
                { key: 'createdAt', title: 'Created', width: '110px', type: 'datetime' },
                { key: 'submitted_at', title: 'Submitted', width: '110px' },
                { key: 'eta', title: 'ETA', width: '110px' }
            ],
            PERMISSION_CODES,
        }
    },
    computed: {
        ...mapState("misc", ["theme", "THEMES", "warehouses"]),
        activeTabLabel() {
            const activeTab = this.purchaseOrderTabs.find((tab) => tab.value === this.selectedTab)
            return activeTab ? activeTab.label : "Open"
        },
        emptyStateMessage() {
            if (this.selectedTab === "completed") return "Completed purchase orders will appear here."
            if (this.selectedTab === "all") return "Get started by creating your first purchase order."
            return "There are no purchase orders awaiting completion."
        },
    },
    components: {
        LinkComponent,
        CustomInput,
        CustomTable,
        SortableTable,
        Paginator,
        AllowPermission,
        PurchaseOrderFilters
    },
    created() {
        // Resolve the route-backed tab before child filters and tables mount so
        // their initial events cannot issue an unfiltered request.
        this.syncTabFromRoute()
    },
    async mounted() {
        this.setData({ pageTitle: "Purchase Orders" })
        await Promise.all([
            this.fetchSuppliers(),
            this.fetchPurchaseOrders()
        ])
    },
    methods: {
        ...mapActions("misc", ["setData"]),

        tabRoute(tab) {
            return {
                path: "/purchase-orders",
                query: { ...this.$route.query, tab },
            }
        },

        syncTabFromRoute() {
            const requestedTab = this.$route.query && this.$route.query.tab
            const activeTab = this.purchaseOrderTabs.find((tab) => tab.value === requestedTab) || this.purchaseOrderTabs[0]
            this.selectedTab = activeTab.value
            this.filters.status = activeTab.status
        },
        
        onFiltersChange(newFilters) {
            this.filters = { ...newFilters }
            this.page = 1
            this.fetchPurchaseOrders()
        },
        
        onTimeFrameChange(newTimeFrame) {
            this.timeFrame = newTimeFrame
            this.page = 1
            this.fetchPurchaseOrders()
        },
        
        onTableSort(sortInfo) {
            this.sortBy = sortInfo.column
            this.sortOrder = sortInfo.order || sortInfo.direction || 'desc' // Check both 'order' and 'direction' properties
            this.page = 1 // Reset to first page when sorting
            this.fetchPurchaseOrders()
        },

        async fetchSuppliers() {
            try {
                const response = await http.get('/purchase-orders/suppliers')
                this.suppliers = response.data.result || []
            } catch (error) {
                console.error("Error fetching suppliers:", error)
                this.suppliers = []
            }
        },

        async fetchPurchaseOrders() {
            try {
                this.loadingData = true
                
                // Build API parameters for enhanced backend
                const params = new URLSearchParams({
                    limit: this.limit.toString(),
                    page: this.page.toString()
                })
                
                // Add search parameter
                if (this.filters.search) {
                    params.append('search', this.filters.search)
                }
                
                // Add status filter
                if (this.filters.status) {
                    params.append('status', this.filters.status)
                }
                
                // Add supplier filter
                if (this.filters.supplier) {
                    params.append('supplier_id', this.filters.supplier)
                }
                
                // Add warehouse filter
                if (this.filters.warehouse) {
                    params.append('warehouse_id', this.filters.warehouse)
                }
                
                // Add date range filters
                if (this.timeFrame.startDate && this.timeFrame.endDate) {
                    params.append('date_range_start', this.timeFrame.startDate)
                    params.append('date_range_end', this.timeFrame.endDate)
                }
                
                // Add sorting parameters
                if (this.sortBy) {
                    params.append('sort_by', this.sortBy)
                    params.append('sort_order', this.sortOrder || 'desc')
                }
                
                const response = await http.get(`/purchase-orders?${params.toString()}`)
                
                // Use backend response directly (no frontend filtering needed)
                this.purchaseOrders = response.data.result || []
                this.totalResults = response.data.totalResults || 0
                this.totalPages = response.data.totalPages || 1
                
            } catch (error) {
                console.error("Error fetching purchase orders:", error)
                this.$toast.error("Error fetching purchase orders")
            } finally {
                this.loadingData = false
            }
        },

        async updatePage(newPage) {
            if (this.totalPages > 0) {
                if (newPage > 0 && newPage <= this.totalPages) {
                    this.page = newPage
                    await this.fetchPurchaseOrders()
                } else {
                    this.$toast.error("Invalid page!")
                }
            }
        },

        formatDate(dateString) {
            if (!dateString) return ''
            return new Date(dateString).toLocaleDateString()
        }
    },
    watch: {
        "$route.query.tab"() {
            this.syncTabFromRoute()
            this.page = 1
            this.fetchPurchaseOrders()
        },
    },
}
</script>

<style lang="scss">
@import "@/assets/variables.scss";

.filter-card {
    background: $lightCardBg;
    &.dark { background: $darkCardBg; h2 { color: $darkTextColor; } }
}
.cselection {
    font-size: 0.8rem;
}
.badge {
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;
}
.badge-success {
    background-color: $successColor;
    color: white;
}
.badge-danger {
    background-color: $dangerColor;
    color: white;
}
.badge-warning {
    background-color: $warningColor;
    color: $textColor;
}

.table-container { width: 100%; overflow-x: auto; border-radius: $cardRadius; box-shadow: $cardShadow; margin-bottom: $sectionGap * 2; border: 1px solid $lightBorderColor; }

.po-link {
    color: $primaryColor;
    text-decoration: none;
    font-weight: 500;
    
    &:hover { text-decoration: underline; color: $secondaryColor; }
}

// Responsive table adjustments
@media (max-width: 1200px) {
    .table-container {
        font-size: 10px;
    }
}

@media (max-width: 768px) {
    .table-container {
        font-size: 9px;
    }
}

.purchase-orders-page { padding: $cardPaddingY; max-width: 100%; }
.purchase-orders-page h3 { margin: 0 0 $sectionGap 0; font-size: 1.25rem; color: $textColor; }

.purchase-orders-toolbar {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 44px;
    margin-bottom: 10px;
}

.toolbar-heading {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;

    h1 {
        margin: 0;
        color: $primaryColor;
        font-size: 1.15rem;
        font-weight: 750;
        line-height: 1.2;
    }
}

.result-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 7px;
    border-radius: 999px;
    background: rgba($primaryColor, 0.08);
    color: rgba($primaryColor, 0.72);
    font-size: 0.7rem;
    font-weight: 800;
}

.purchase-order-tabs {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: min(100%, 290px);
    gap: 2px;
    padding: 3px;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    background: rgba($lightBorderColor, 0.28);
}

.purchase-order-tab {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    min-height: 36px;
    padding: 7px 14px;
    border-radius: 6px;
    color: rgba($textColor, 0.72);
    text-decoration: none;
    transition: background-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;

    span { font-size: 0.85rem; font-weight: 700; }

    &:hover {
        color: $primaryColor;
        text-decoration: none;
    }

    &:focus-visible {
        outline: 2px solid $secondaryColor;
        outline-offset: 2px;
    }
}

.purchase-order-tab--active {
    background: $lightCardBg;
    color: $primaryColor;
    box-shadow: 0 1px 4px rgba($textColor, 0.12);
}

.create-order-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 38px;
    margin-left: auto;
    padding: 7px 14px;
    border-radius: 7px;
    color: #fff;
    font-size: 0.8rem;
    font-weight: 700;
    line-height: 1;
    text-decoration: none;
    white-space: nowrap;

    .material-icons { font-size: 17px; }
    &:hover { color: #fff; text-decoration: none; }
}

@media (max-width: 900px) {
    .purchase-orders-toolbar { flex-wrap: wrap; }
    .create-order-btn { order: 2; }
    .purchase-order-tabs { order: 3; width: 100%; }
}

@media (max-width: 600px) {
    .purchase-orders-toolbar { gap: 8px; }
    .toolbar-heading { flex: 1; }
    .create-order-btn { padding-inline: 10px; }
    .purchase-order-tab { padding-inline: 8px; }
}

.btn-secondary {
    background-color: $secondaryColor;
    border: 1px solid $secondaryColor;
    
    &:hover {
        background-color: darken($secondaryColor, 6%);
        border-color: darken($secondaryColor, 6%);
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba($secondaryColor, 0.3);
    }
    
    &:active {
        transform: translateY(0);
        box-shadow: 0 2px 4px rgba($secondaryColor, 0.2);
    }
}
</style>
